import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { bp, megaText } from 'Styles/helpers';
import { rem } from 'polished';
import screen from 'superior-mq';
import Container from '../../container';
import Button from '../../button';
import Waves from '../../waves';
import DetailImage from '../detail-image';
import DetailGraphic from '../detail-graphic';
import ImageSlideIn from '../../image-slide-in';

const imageLeft = `
  top: -100px;
  left: calc(50% - 300px);
  width: 441px;
  height: 294px;
  transform: rotate(5.7deg) translateX(-50%);

  ${screen.below(bp.tablet, `
    top: -125px;
    left: calc(50% - 225px);
    max-width: 330px;
  `)};

  ${screen.below(bp.mobileMid, `
    top: -75px;
    left: calc(50% - 150px);
    max-width: 220px;
  `)};
`;

const imageCenter = `
  top: 90px;
  left: 50%;
  width: 508px;
  height: 338px;
  transform: rotate(-6.7deg) translateX(-50%);

  ${screen.below(bp.tablet, `
    top: 0;
    max-width: 381px;
  `)};

  ${screen.below(bp.mobileMid, `
    max-width: 254px;
  `)};
`;

const imageRight = `
  top: -120px;
  left: calc(50% + 300px);
  width: 441px;
  height: 294px;
  transform: rotate(5.7deg) translateX(-50%);

  ${screen.below(bp.tablet, `
    top: -150px;
    left: calc(50% + 225px);
    max-width: 330px;
  `)};

  ${screen.below(bp.mobileMid, `
    top: -100px;
    left: calc(50% + 150px);
    max-width: 220px;
  `)};
`;

const DetailStart = ({
  content,
  isDark,
  waveColor,
  icon,
  fill,
  fill2,
}) => {
  if (!content) return null;

  const [image1, image2, image3] = content.images || [];
  const imageParams = '&q=84&fit=thumb&fm=jpg&fl=progressive';

  return (
    <StartFundraiserSection isDark={isDark}>
      <Container>
        <ImageSlideIn>
          <Content isDark={isDark}>

            <Graphics>
              <DetailGraphic icon={icon} fill={fill} fill2={fill2} />
            </Graphics>

            {content.images && content.images.length > 0 &&
              <Fragment>

                {image1 && image1.fields && image1.fields.file &&
                  <DetailImage
                    width={440}
                    height={294}
                    src={`${image1.fields.file.url}?w=440&h=294${imageParams}`}
                    alt={image1.fields.description}
                    styles={imageLeft}
                  />
                }

                {image2 && image2.fields && image2.fields.file &&
                  <DetailImage
                    width={508}
                    height={338}
                    src={`${image2.fields.file.url}?w=508&h=338${imageParams}`}
                    alt={image2.fields.description}
                    styles={imageCenter}
                  />
                }

                {image3 && image3.fields && image3.fields.file &&
                  <DetailImage
                    width={440}
                    height={294}
                    src={`${image3.fields.file.url}?w=440&h=294${imageParams}`}
                    alt={image3.fields.description}
                    styles={imageRight}
                  />
                }

                </Fragment>
              }

            <Headline isDark={isDark}>Clean Water</Headline>
          </Content>
        </ImageSlideIn>
        {content.ctaHeading &&
          <StartFundraiserText>{content.ctaHeading}</StartFundraiserText>
        }

        {content.callToAction &&
          <Button href={content.callToAction.fields.linkUrl}>
            {content.callToAction.fields.linkText}
          </Button>
        }

      </Container>
      <Waves
        position="bottom"
        alignment="bottom"
        height={330}
        color={waveColor}
      />
    </StartFundraiserSection>
  );
};

const StartFundraiserSection = styled.section`
  position: relative;
  padding: 380px 0 445px;
  color: ${props => props.isDark ? '#fff' : '#000'};
  background-color: ${props => props.isDark ? '#073342' : '#fff'};
  text-align: center;
  overflow: hidden;

  ${screen.below(bp.tablet, `
    padding: 380px 0 236px;
  `)};

  ${screen.below(bp.mobileMid, `
    padding: 236px 0 190px;
  `)};
`;

const Headline = styled.h2`
  ${megaText()}
  margin-bottom: 300px;
  color: ${props => props.theme.blue};
  backface-visibility: hidden;

  @supports (mix-blend-mode: multiply) {
    mix-blend-mode: multiply;
  }

  ${screen.below(bp.tablet, `
    margin-bottom: 250px;
  `)};

  ${screen.below(bp.mobileMid, `
    ${megaText(8.1)}
    margin-bottom: 190px;
  `)};

  ${screen.below(bp.mobileRealSm, `
    ${megaText(10)}
  `)}
`;

const Content = styled.div`
  position: relative;
  background-color: ${props => props.isDark ? '#073342' : '#fff'};
  z-index: 1;
`;

const Graphics = styled.div`
  position: absolute;
  top: -124px;
  left: 50%;
  z-index: 1;
  transform: translateX(-50%);

  svg {
    transform: translate(-50%, -50%);
  }

  ${screen.below(bp.mobileMid, `
    top: -90px;
  `)};
`;

const StartFundraiserText = styled.h5`
  margin-bottom: 50px;
  font-family: ${props => props.theme.sansFont};
  font-size: ${rem(44)};

  ${screen.below(bp.mobileSm, `
    margin-bottom: 25px;
    font-size: ${rem(25)};
  `)}
`;

DetailStart.propTypes = {
  content: PropTypes.object,
  isDark: PropTypes.bool,
  waveColor: PropTypes.string,
  icon: PropTypes.string,
  fill: PropTypes.string,
  fill2: PropTypes.string,
};

DetailStart.defaultProps = {
  waveColor: '#fff',
  icon: 'drop',
  fill: '#69a4b8',
  fill2: '#69a4b8',
};

export default DetailStart;
